import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, Mail, ArrowLeft, Search, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_TONE = {
    'Hot Lead': 'var(--accent)',
    'Warm Lead': 'var(--ink)',
    'Nurture Lead': 'var(--ink-dim)',
};

export default function AdminSnapshots() {
    const [password, setPassword] = useState(
        () => sessionStorage.getItem('ca_admin_pw') || ''
    );
    const [authed, setAuthed] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState('');

    const verifyAndLoad = useCallback(async (pw) => {
        setAuthError(null);
        setLoading(true);
        try {
            await axios.post(`${API}/admin/verify`, {}, { headers: { 'X-Admin-Password': pw } });
            const res = await axios.get(`${API}/admin/snapshots`, {
                headers: { 'X-Admin-Password': pw },
            });
            setItems(res.data.items || []);
            setAuthed(true);
            sessionStorage.setItem('ca_admin_pw', pw);
        } catch (err) {
            setAuthed(false);
            if (err?.response?.status === 401) setAuthError('Invalid password.');
            else setAuthError('Could not load snapshots. Check connection and try again.');
            sessionStorage.removeItem('ca_admin_pw');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (password && !authed) verifyAndLoad(password);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = () => verifyAndLoad(password);

    const handleLogin = (e) => {
        e.preventDefault();
        verifyAndLoad(password);
    };

    const filtered = items.filter((it) => {
        if (!search) return true;
        const s = search.toLowerCase();
        const hay = [
            it.contact?.name,
            it.contact?.email,
            it.contact?.business_name,
            it.answers?.business_type,
            it.recommendation?.primary_system,
            it.recommendation?.lead_score,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(s);
    });

    if (!authed) {
        return (
            <div className="min-h-screen paper grain grid place-items-center px-5">
                <form
                    onSubmit={handleLogin}
                    data-testid="admin-login-form"
                    className="w-full max-w-md"
                    style={{ border: '1px solid var(--ink)', boxShadow: '6px 6px 0 0 var(--ink)' }}
                >
                    <div className="px-6 py-3 border-b flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]" style={{ borderColor: 'var(--ink)' }}>
                        <span className="flex items-center gap-2"><Lock className="w-3 h-3" /> Restricted</span>
                        <span className="text-[var(--accent)]">ADMIN</span>
                    </div>
                    <div className="px-6 py-7">
                        <h1 className="font-display font-medium text-[2.4rem] leading-[0.98] tracking-[-0.03em]">
                            Snapshot{' '}
                            <span className="italic-display">inbox.</span>
                        </h1>
                        <p className="mt-3 font-serif-body italic text-[15px] text-[var(--ink-2)]">
                            Enter the admin password to view submitted Growth System Snapshots.
                        </p>

                        <label className="block mt-6">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--ink-mute)]">
                                Password
                            </span>
                            <input
                                type="password"
                                data-testid="admin-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                autoFocus
                                className="mt-2 w-full bg-transparent outline-none font-mono text-[16px] py-2"
                                style={{ borderBottom: '1px solid var(--ink)' }}
                            />
                        </label>

                        {authError && (
                            <p data-testid="admin-auth-error" className="mt-3 font-serif-body text-[13px] text-[var(--accent)]">
                                {authError}
                            </p>
                        )}

                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            disabled={loading || !password}
                            className="btn-primary mt-7 w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Verifying…' : 'Unlock'}
                        </button>
                        <Link to="/" className="btn-link mt-6 inline-flex">
                            <ArrowLeft className="w-3.5 h-3.5" />
                            Back to portfolio
                        </Link>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen paper grain pb-20" data-testid="admin-snapshots-page">
            <div
                className="sticky top-0 inset-x-0 z-30 paper border-b"
                style={{ borderColor: 'var(--ink)' }}
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
                    <Link to="/" className="btn-link">
                        <ArrowLeft className="w-4 h-4" />
                        Back to portfolio
                    </Link>
                    <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.22em]">
                        <span>{items.length} SNAPSHOT{items.length === 1 ? '' : 'S'}</span>
                        <button
                            type="button"
                            onClick={refresh}
                            data-testid="admin-refresh-button"
                            className="btn-ghost !py-1.5 !px-3 !text-[11px]"
                        >
                            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]">
                    §VIII — Inbox
                </p>
                <h1 className="mt-3 font-display font-medium text-[3rem] md:text-[4.4rem] leading-[0.95] tracking-[-0.04em]">
                    Growth System{' '}
                    <span className="italic-display">snapshots.</span>
                </h1>

                <div className="mt-8 flex items-center gap-2 max-w-md py-2 border-b" style={{ borderColor: 'var(--ink)' }}>
                    <Search className="w-4 h-4 text-[var(--ink-mute)]" />
                    <input
                        type="text"
                        data-testid="admin-search-input"
                        placeholder="Search by name, email, business, score…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 bg-transparent outline-none font-serif-body text-[15px] placeholder:text-[var(--ink-mute)]"
                    />
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-8">
                    <div data-testid="admin-snapshots-list">
                        {filtered.length === 0 ? (
                            <div className="p-10 text-center font-serif-body italic text-[var(--ink-2)] border" style={{ borderColor: 'var(--ink)' }}>
                                No snapshots found.
                            </div>
                        ) : (
                            <div
                                className="max-h-[72vh] overflow-y-auto"
                                style={{ border: '1px solid var(--ink)' }}
                            >
                                {filtered.map((it, i) => (
                                    <button
                                        key={it.id}
                                        type="button"
                                        data-testid={`admin-row-${it.id}`}
                                        onClick={() => setSelected(it)}
                                        className="w-full text-left p-4 transition-colors hover:bg-[var(--paper-soft)] flex flex-col gap-1.5"
                                        style={{
                                            borderTop: i === 0 ? 'none' : '1px solid rgba(21,17,13,0.18)',
                                            background:
                                                selected?.id === it.id ? 'var(--accent-soft)' : 'transparent',
                                        }}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-display font-medium text-[18px] tracking-[-0.02em]">
                                                {it.contact?.business_name || it.contact?.name || 'Unknown'}
                                            </span>
                                            <span
                                                className="font-mono text-[10px] uppercase tracking-[0.22em]"
                                                style={{ color: SCORE_TONE[it.recommendation?.lead_score] || 'var(--ink)' }}
                                            >
                                                {it.recommendation?.lead_score || '—'}
                                            </span>
                                        </div>
                                        <div className="font-serif-body text-[13px] text-[var(--ink-2)]">
                                            {it.contact?.email} · {it.answers?.business_type}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-mute)] flex items-center gap-3 flex-wrap">
                                            <span className="text-[var(--accent)]">{it.recommendation?.primary_system}</span>
                                            <span>·</span>
                                            <span>{it.answers?.timeline}</span>
                                            <span>·</span>
                                            <span>{it.answers?.budget}</span>
                                            <span className="ml-auto">{formatDate(it.created_at)}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div data-testid="admin-snapshot-detail">
                        {selected ? (
                            <DetailPanel item={selected} />
                        ) : (
                            <div
                                className="p-10 font-serif-body italic text-[var(--ink-2)] h-full grid place-items-center text-center"
                                style={{ border: '1px solid var(--ink)' }}
                            >
                                Select a snapshot on the left to view the full
                                answers and recommendation.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function DetailPanel({ item }) {
    const { contact, answers, recommendation } = item;
    return (
        <div style={{ border: '1px solid var(--ink)' }}>
            <div className="px-5 py-3 border-b flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]" style={{ borderColor: 'var(--ink)' }}>
                <span>{formatDate(item.created_at)}</span>
                <span style={{ color: SCORE_TONE[recommendation.lead_score] || 'var(--ink)' }}>
                    {recommendation.lead_score}
                </span>
            </div>

            <div className="p-5 md:p-7 space-y-7">
                <div>
                    <h3 className="font-display font-medium text-[1.8rem] leading-[1.05] tracking-[-0.025em]">
                        {contact.business_name || contact.name}
                    </h3>
                    <p className="font-serif-body italic text-[15px] text-[var(--ink-2)]">
                        {contact.name} · {contact.email}
                    </p>
                    <a
                        href={`mailto:${contact.email}?subject=Your%20Growth%20System%20Snapshot`}
                        className="btn-ghost mt-4 !py-2 !px-3.5 !text-[12px]"
                    >
                        <Mail className="w-3.5 h-3.5" />
                        Reply to {contact.name.split(' ')[0]}
                    </a>
                </div>

                <Section title="Recommendation">
                    <Row k="Primary" v={recommendation.primary_system} accent />
                    <Row k="Secondary" v={recommendation.secondary_system} />
                    <Row k="Build path" v={recommendation.build_path} />
                    <Row k="Bottleneck" v={recommendation.main_bottleneck} multi />
                </Section>

                <Section title="Answers">
                    <Row k="Business type" v={answers.business_type} />
                    <Row k="Current setup" v={(answers.current_setup || []).join(', ') || '—'} />
                    <Row k="Main goal" v={answers.main_goal} />
                    <Row k="Biggest leak" v={answers.biggest_leak} />
                    <Row k="Lead sources" v={(answers.lead_sources || []).join(', ') || '—'} />
                    <Row k="After enquiry" v={answers.after_enquiry || '—'} />
                    <Row k="Timeline" v={answers.timeline} />
                    <Row k="Budget" v={answers.budget} />
                </Section>

                <Section title="Contact">
                    <Row k="Email" v={contact.email} />
                    <Row k="Business" v={contact.business_name || '—'} />
                    <Row k="Website" v={
                        contact.website ? (
                            <a
                                href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                                target="_blank"
                                rel="noreferrer"
                                className="border-b border-[var(--ink)] inline-flex items-center gap-1"
                            >
                                {contact.website}
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        ) : '—'
                    } />
                    <Row k="Phone" v={contact.phone || '—'} />
                </Section>

                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--ink-mute)] pt-3 border-t" style={{ borderColor: 'rgba(21,17,13,0.2)' }}>
                    ID {item.id} · {item.email_sent ? 'notification sent' : 'no email sent'}
                </p>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] pb-2 border-b" style={{ borderColor: 'var(--ink)' }}>
                {title}
            </p>
            <div className="mt-3 space-y-2">{children}</div>
        </div>
    );
}

function Row({ k, v, accent, multi }) {
    return (
        <div className={`grid ${multi ? 'grid-cols-1 gap-1' : 'grid-cols-[7rem_1fr] gap-3'} font-serif-body text-[14px]`}>
            <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-[var(--ink-mute)]">{k}</span>
            <span className={accent ? 'text-[var(--accent)] font-display font-medium text-[16px] tracking-[-0.02em]' : 'text-[var(--ink)]'}>
                {v}
            </span>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}
